import { Fancybox } from "@fancyapps/ui/dist/fancybox";

const Masonry = require('masonry-layout');

function getHeaderOffset() {
  var header = document.getElementById('header');
  var headerHeight = header ? header.offsetHeight : 0;

  return headerHeight + 24;
}

function getTargetIdFromLink(link) {
  var href = link.getAttribute('href') || '';

  if (href.indexOf('#') !== 0 || href.length <= 1) {
    return '';
  }

  return decodeURIComponent(href.substring(1));
}

function setActiveLink(gallery, targetId) {
  if (!targetId) {
    return;
  }

  var links = gallery.querySelectorAll('[data-gallery-nav-link]');

  links.forEach(function (link) {
    link.classList.toggle('is-active', getTargetIdFromLink(link) === targetId);
  });
}

function scrollToSection(section) {
  var top = section.getBoundingClientRect().top + window.pageYOffset - getHeaderOffset();
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  window.scrollTo({
    top: top,
    behavior: reducedMotion ? 'auto' : 'smooth',
  });
}

function ensureMasonryHelpers(grid) {
  if (!grid.querySelector('.c-gallery-archive__sizer')) {
    var sizer = document.createElement('div');
    sizer.className = 'c-gallery-archive__sizer';
    sizer.setAttribute('aria-hidden', 'true');
    grid.insertBefore(sizer, grid.firstChild);
  }

  if (!grid.querySelector('.c-gallery-archive__gutter')) {
    var gutter = document.createElement('div');
    gutter.className = 'c-gallery-archive__gutter';
    gutter.setAttribute('aria-hidden', 'true');
    grid.insertBefore(gutter, grid.firstChild ? grid.firstChild.nextSibling : null);
  }
}

function createRafThrottle(callback) {
  var ticking = false;

  return function () {
    if (ticking) {
      return;
    }

    ticking = true;

    window.requestAnimationFrame(function () {
      ticking = false;
      callback();
    });
  };
}

function markImageLoaded(image, masonry) {
  var card = image.closest('.c-gallery-archive__card');

  image.classList.add('is-loaded');

  if (card) {
    card.classList.add('is-loaded');
  }

  if (masonry && typeof masonry.layout === 'function') {
    masonry.layout();
  }
}

function loadImage(image, masonry) {
  var src = image.getAttribute('data-src');

  if (!src) {
    if (image.complete) {
      markImageLoaded(image, masonry);
    }

    return;
  }

  image.addEventListener('load', function () {
    markImageLoaded(image, masonry);
  }, { once: true });

  image.addEventListener('error', function () {
    var card = image.closest('.c-gallery-archive__card');

    image.classList.add('has-error');

    if (card) {
      card.classList.add('has-error');
    }

    if (masonry && typeof masonry.layout === 'function') {
      masonry.layout();
    }
  }, { once: true });

  image.setAttribute('src', src);
  image.removeAttribute('data-src');
}

function initLazyImages(grid, masonry) {
  var images = Array.prototype.slice.call(grid.querySelectorAll('.c-gallery-archive__img[data-src]'));

  if (!images.length) {
    return;
  }

  if (!('IntersectionObserver' in window)) {
    images.forEach(function (image) {
      loadImage(image, masonry);
    });

    return;
  }

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) {
        return;
      }

      observer.unobserve(entry.target);
      loadImage(entry.target, masonry);
    });
  }, {
    rootMargin: '1000px 0px',
    threshold: 0,
  });

  images.forEach(function (image) {
    observer.observe(image);
  });
}

function initMasonryGrid(grid) {
  if (grid._tqGalleryMasonry) {
    return grid._tqGalleryMasonry;
  }

  ensureMasonryHelpers(grid);

  var masonry = new Masonry(grid, {
    itemSelector: '.c-gallery-archive__card',
    columnWidth: '.c-gallery-archive__sizer',
    gutter: '.c-gallery-archive__gutter',
    percentPosition: true,
    horizontalOrder: false,
    transitionDuration: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : '0.24s',
  });

  grid._tqGalleryMasonry = masonry;

  initLazyImages(grid, masonry);

  var layout = createRafThrottle(function () {
    masonry.layout();
  });

  window.addEventListener('load', layout);
  window.addEventListener('resize', layout);

  // Primeros recalculos: ayudan a que las anclas sean estables justo al cargar.
  window.requestAnimationFrame(layout);
  setTimeout(layout, 250);
  setTimeout(layout, 800);

  return masonry;
}

function initGrids(grids) {
  grids.forEach(function (grid) {
    initMasonryGrid(grid);
  });
}

function initGridsInSection(section) {
  var grids = Array.prototype.slice.call(section.querySelectorAll('.c-gallery-archive__grid'));

  initGrids(grids);
}

function initLazyGrids(grids) {
  if (!grids.length) {
    return;
  }

  if (!('IntersectionObserver' in window)) {
    initGrids(grids);
    return;
  }

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) {
        return;
      }

      observer.unobserve(entry.target);
      initMasonryGrid(entry.target);
    });
  }, {
    rootMargin: '1400px 0px',
    threshold: 0,
  });

  grids.forEach(function (grid) {
    observer.observe(grid);
  });
}

var lastGalleryLightboxTrigger = null;

function getTriggerDownloadData(trigger) {
  var url = '';
  var filename = '';
  var type = '';

  if (trigger) {
    url = trigger.getAttribute('data-download-src') || trigger.getAttribute('href') || '';
    filename = trigger.getAttribute('data-download-filename') || '';
    type = trigger.getAttribute('data-type') || '';
  }

  if (!url) {
    return null;
  }

  return {
    url: url,
    filename: filename,
    isVideo: type === 'html5video' || /\.mp4(?:[?#].*)?$/i.test(url),
  };
}

function getCurrentFancyboxSlide() {
  if (typeof Fancybox.getSlide === 'function') {
    return Fancybox.getSlide();
  }

  if (typeof Fancybox.getInstance !== 'function') {
    return null;
  }

  var instance = Fancybox.getInstance();

  if (!instance) {
    return null;
  }

  if (typeof instance.getSlide === 'function') {
    return instance.getSlide();
  }

  var carousel = instance.Carousel || instance.carousel;

  if (!carousel) {
    return null;
  }

  if (typeof carousel.getSlide === 'function') {
    return carousel.getSlide();
  }

  if (Array.isArray(carousel.slides)) {
    var index = typeof carousel.page === 'number' ? carousel.page : carousel.pageIndex;

    if (typeof index === 'number') {
      return carousel.slides[index] || null;
    }
  }

  return null;
}

function getSlideDownloadData(slide) {
  var triggerData = getTriggerDownloadData(slide && slide.triggerEl);

  if (triggerData) {
    return triggerData;
  }

  if (!slide || !slide.src) {
    return null;
  }

  return {
    url: slide.src,
    filename: '',
    isVideo: slide.type === 'html5video' || /\.mp4(?:[?#].*)?$/i.test(slide.src),
  };
}

function getCurrentGalleryDownloadData() {
  return getSlideDownloadData(getCurrentFancyboxSlide()) || getTriggerDownloadData(lastGalleryLightboxTrigger);
}

function isCurrentFancyboxContentVideo(container) {
  var currentSlide = container.querySelector('.fancybox__slide.is-selected')
    || container.querySelector('.fancybox__slide[aria-hidden="false"]')
    || container.querySelector('.is-selected');

  if (!currentSlide) {
    return false;
  }

  return currentSlide.classList.contains('has-html5video')
    || currentSlide.classList.contains('has-youtube')
    || currentSlide.classList.contains('has-vimeo')
    || !!currentSlide.querySelector('video');
}

function downloadGalleryItem(data) {
  if (!data) {
    return;
  }

  var link = document.createElement('a');

  link.href = data.url;

  if (data.filename) {
    link.setAttribute('download', data.filename);
  } else {
    link.setAttribute('download', '');
  }

  document.body.appendChild(link);
  link.click();
  link.remove();
}

function syncVideoDownloadButton() {
  var container = document.querySelector('.fancybox__container');

  if (!container) {
    return;
  }

  var button = container.querySelector('[data-gallery-fancybox-video-download]');

  if (!button) {
    button = document.createElement('button');
    button.className = 'f-button c-gallery-fancybox-video-download';
    button.type = 'button';
    button.title = 'Descargar vídeo';
    button.setAttribute('aria-label', 'Descargar vídeo');
    button.setAttribute('data-gallery-fancybox-video-download', '');
    button.innerHTML = '<svg tabindex="-1" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3v12m0 0 5-5m-5 5-5-5M5 19h14"/></svg><span>Descargar vídeo</span>';

    button.addEventListener('click', function () {
      if (!isCurrentFancyboxContentVideo(container)) {
        return;
      }

      downloadGalleryItem(getCurrentGalleryDownloadData());
    });

    container.appendChild(button);
  }

  var data = getCurrentGalleryDownloadData();

  button.hidden = !data || !data.isVideo || !isCurrentFancyboxContentVideo(container);
}

function queueVideoDownloadButtonSync() {
  window.requestAnimationFrame(function () {
    syncVideoDownloadButton();
  });
}

function initGalleryVideoDownloadButton(gallery) {
  gallery.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-gallery-lightbox]');

    if (!trigger || !gallery.contains(trigger)) {
      return;
    }

    lastGalleryLightboxTrigger = trigger;
    setTimeout(syncVideoDownloadButton, 0);
    setTimeout(syncVideoDownloadButton, 150);
    setTimeout(syncVideoDownloadButton, 450);
  });

  var observer = new MutationObserver(queueVideoDownloadButtonSync);

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

function initGalleryFancybox(gallery) {
  Fancybox.bind(gallery, '[data-gallery-lightbox]', {
    Hash: false,
    dragToClose: true,
    Carousel: {
      Toolbar: {
        display: {
          left: ['counter'],
          middle: ['zoomIn', 'zoomOut', 'toggle1to1'],
          right: ['download', 'thumbs', 'close'],
        },
      },
    },
  });
}

function initGalleryNav(gallery) {
  var links = gallery.querySelectorAll('[data-gallery-nav-link]');
  var sections = gallery.querySelectorAll('.c-gallery-archive__section, .c-gallery-archive__subsection, .c-gallery-archive__content');

  if (!links.length || !sections.length) {
    return;
  }

  links.forEach(function (link) {
    link.addEventListener('click', function (event) {
      var targetId = getTargetIdFromLink(link);
      var target = targetId ? document.getElementById(targetId) : null;

      if (!target || !gallery.contains(target)) {
        return;
      }

      event.preventDefault();
      setActiveLink(gallery, targetId);
      initGridsInSection(target);

      window.requestAnimationFrame(function () {
        scrollToSection(target);
      });

      if (history.pushState) {
        history.pushState(null, '', '#' + encodeURIComponent(targetId));
      }
    });
  });

  if (!('IntersectionObserver' in window)) {
    return;
  }

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting && entry.target.id) {
        setActiveLink(gallery, entry.target.id);
      }
    });
  }, {
    rootMargin: '-' + getHeaderOffset() + 'px 0px -62% 0px',
    threshold: 0,
  });

  sections.forEach(function (section) {
    observer.observe(section);
  });
}

function initHashTarget(gallery) {
  if (!window.location.hash || window.location.hash.length <= 1) {
    return;
  }

  var targetId = decodeURIComponent(window.location.hash.substring(1));
  var target = document.getElementById(targetId);

  if (!target || !gallery.contains(target)) {
    return;
  }

  setActiveLink(gallery, targetId);
  initGridsInSection(target);

  window.requestAnimationFrame(function () {
    scrollToSection(target);
  });
}

export default function initGalleryArchive() {
  var galleries = document.querySelectorAll('[data-gallery-archive]');

  if (!galleries.length) {
    return;
  }

  galleries.forEach(function (gallery) {
    var grids = Array.prototype.slice.call(gallery.querySelectorAll('.c-gallery-archive__grid'));

    initLazyGrids(grids);

    initGalleryFancybox(gallery);
    initGalleryVideoDownloadButton(gallery);
    initGalleryNav(gallery);
    initHashTarget(gallery);
  });
}
